import {ICountryArray} from 'data/country';
import {ICognitoType, IActiveUser, IUsageDateType} from 'interfaces/interface';
import {IModalState, IRegisterInfo} from 'pages/Register';
import {SetterOrUpdater} from 'recoil';
import {IUserInfo} from 'utils/Atoms';
import {s3GetCognito} from 'utils/cognito';
import {
  changeAgreementStatus,
  createAccount,
  createTempAccessToken,
  createUserProfile,
  defaultProps,
  initSignature,
  signIn,
  updatePassword,
} from './api';
import {isSignInValidate} from './validate';
import {createUsageExcelData} from 'utils/excel/handler/usage/utils/create-usage';
import {UsageDataDto} from 'utils/excel/handler/usage/dto/usage-data.dto';
import {UsageRegistersService} from 'utils/excel/handler/usage/usage-registers.service';

/**
 * 샘플 함수
 * 컨버터 샘플 함수입니다.
 */
export const sample = () => {
  // Converter sample function
};

/**
 * 버튼 액션 인터페이스
 * 회원가입 버튼 액션에 필요한 파라미터를 정의합니다.
 * @property {boolean} enableButton - 버튼 활성화 여부
 * @property {number} step - 현재 단계
 * @property {ICountryArray} selectCountry - 선택된 국가
 * @property {IRegisterInfo} registerInfo - 회원가입 정보
 * @property {React.Dispatch<React.SetStateAction<{}>>} setErrorModal - 에러 모달 설정 함수
 * @property {React.Dispatch<React.SetStateAction<boolean>>} setIsLoading - 로딩 상태 설정 함수
 * @property {React.Dispatch<React.SetStateAction<number>>} setStep - 단계 설정 함수
 * @property {React.Dispatch<React.SetStateAction<boolean>>} setEnableButton - 버튼 활성화 설정 함수
 * @property {Blob | undefined} signature - 서명 이미지
 * @property {Blob | undefined} signature_thumbnail - 서명 썸네일 이미지
 * @property {React.Dispatch<React.SetStateAction<IModalState>>} setModalState - 모달 상태 설정 함수
 */
export interface IButtonAction {
  enableButton: boolean;
  step: number;
  selectCountry: ICountryArray;
  registerInfo: IRegisterInfo;
  setErrorModal: React.Dispatch<React.SetStateAction<{}>>;
  setIsLoading: React.Dispatch<React.SetStateAction<boolean>>;
  setStep: React.Dispatch<React.SetStateAction<number>>;
  setEnableButton: React.Dispatch<React.SetStateAction<boolean>>;
  signature: Blob | undefined;
  signature_thumbnail: Blob | undefined;
  setModalState: React.Dispatch<React.SetStateAction<IModalState>>;
}

/**
 * 회원가입 버튼 액션
 * 회원가입 단계별 버튼 액션을 처리합니다.
 * @param {IButtonAction} params - 버튼 액션 파라미터
 * @returns {Promise<void>}
 */
export const buttonAction = async ({
  enableButton,
  step,
  selectCountry,
  registerInfo,
  setErrorModal,
  setIsLoading,
  setStep,
  setEnableButton,
  signature,
  signature_thumbnail,
  setModalState,
}: IButtonAction) => {
  if (enableButton) {
    let apiResult = false;
    if (step === 0) {
      apiResult = await createAccount({
        locale: selectCountry.languageCode,
        phone: registerInfo.phone,
        setIsLoading,
        setErrorModal,
      });
    } else if (step === 1) {
      apiResult = await updatePassword({
        registerInfo,
        setIsLoading,
        setErrorModal,
      });
    } else if (step === 2) {
      apiResult = await createUserProfile({
        registerInfo,
        setIsLoading,
        setErrorModal,
      });
    } else if (step === 3) {
      apiResult = await changeAgreementStatus({
        registerInfo,
        setIsLoading,
        setErrorModal,
      });
    } else if (step === 4) {
      const tempAccessToken = await createTempAccessToken({
        phone: registerInfo.phone,
        setErrorModal,
        setIsLoading,
      });

      if (tempAccessToken) {
        const result = await initSignature({
          registerInfo,
          signature,
          signature_thumbnail,
          setIsLoading,
          setErrorModal,
          authorization: tempAccessToken,
        });

        if (result) {
          setModalState({
            confirm: {state: '', open: true},
            tempInfo: {state: '', open: false},
          });
        }
      }
      return;
    } else {
      return;
    }

    if (apiResult) {
      setStep((prev: number) => prev + 1);
      setEnableButton(false);
    }
  }
};

/**
 * 로그인 인터페이스
 * 로그인에 필요한 파라미터를 정의합니다.
 * @property {React.Dispatch<React.SetStateAction<boolean>>} setIsLoading - 로딩 상태 설정 함수
 * @property {string} password - 비밀번호
 * @property {string} id - 아이디 (전화번호)
 * @property {SetterOrUpdater<IUserInfo | null>} setUserInfo - 사용자 정보 설정 함수
 * @property {boolean} autoLogin - 자동 로그인 여부
 * @property {React.Dispatch<React.SetStateAction<{}>>} setErrorModal - 에러 모달 설정 함수
 * @property {React.Dispatch<React.SetStateAction<IModalState>>} setModalState - 모달 상태 설정 함수
 */
interface IDoLogin {
  setIsLoading: React.Dispatch<React.SetStateAction<boolean>>;
  password: string;
  id: string;
  setUserInfo: SetterOrUpdater<IUserInfo | null>;
  autoLogin: boolean;
  setErrorModal: React.Dispatch<React.SetStateAction<{}>>;
  setModalState: React.Dispatch<React.SetStateAction<IModalState>>;
}

/**
 * 로그인 함수
 * 사용자 로그인을 처리합니다.
 * @param {IDoLogin} params - 로그인 파라미터
 * @returns {Promise<any | false>} 성공 시 사용자 정보, 실패 시 false
 */
export const doLogin = async ({
  setIsLoading,
  password,
  id,
  setUserInfo,
  autoLogin,
  setErrorModal,
  setModalState,
}: IDoLogin) => {
  setIsLoading(true);
  const phone = id.replace(/ /gi, '');
  const errors = await isSignInValidate(phone, password);
  if (errors.length !== 0) {
    setIsLoading(false);
    setErrorModal({
      onOff: true,
      title: 'modalErrorLoginTitle',
      detail: 'modalErrorLoginDetail',
    });
    return false;
  } else {
    try {
      const requestBody = {
        phone: phone,
        password: password,
      };

      const response = await signIn({
        ...requestBody,
        setIsLoading,
        setErrorModal,
      });

      if (response?.accessToken) {
        setUserInfo(response);
        if (autoLogin) {
          localStorage.setItem(
            'userInfo',
            JSON.stringify({
              info: {
                ...response,
                autoLogin: autoLogin,
              },
            }),
          );
        } else {
          sessionStorage.setItem(
            'userInfo',
            JSON.stringify({
              info: {
                ...response,
                autoLogin: autoLogin,
              },
            }),
          );
        }
        setIsLoading(false);
        return response;
      } else {
        setModalState({
          tempInfo: {
            state: response.user.state,
            open: true,
            info: response.user,
          },
        });
        setIsLoading(false);
      }
    } catch (error: any) {
      setIsLoading(false);
      return false;
    }
  }
};

/**
 * 객체 타입
 * 임의의 키-값 쌍을 가진 객체 타입입니다.
 */
export interface MyObject {
  [key: string]: any;
}

/**
 * 객체의 존재하는 key만 업데이트 함수
 * 객체에 존재하는 키만 업데이트합니다.
 * @param {IRegisterInfo} obj - 업데이트할 객체
 * @param {MyObject} updates - 업데이트할 값들
 * @returns {IRegisterInfo} 업데이트된 객체
 */
export const updateExistingKeys = (
  obj: IRegisterInfo,
  updates: MyObject,
): IRegisterInfo => {
  // 객체의 키를 반복
  Object.keys(updates).forEach((key: string) => {
    // 해당 키가 obj에 존재하는지 확인
    if (Object.prototype.hasOwnProperty.call(obj, key)) {
      // 존재하는 경우에만 값을 수정
      obj[key] = updates[key];
    }
  });
  return obj;
};

/**
 * 총 게시물 수와 한 페이지 당 보여질 게시물 개수를 사용하여 총 페이지 개수를 계산하는 함수
 * @param {number} totalCount - 총 게시물 수
 * @param {number} countPerPage - 한 페이지 당 보여질 게시물 개수
 * @returns {number} 총 페이지 개수
 * @example
 * ```typescript
 * const result = convertTotalPage(34, 10);
 * // result: 4
 * ```
 */
export const convertTotalPage = (
  totalCount: number,
  countPerPage: number,
): number => {
  let totalPage = 1;
  if (totalCount) {
    totalPage = Math.ceil(totalCount / countPerPage);
  }
  return totalPage;
};

/**
 * 유니코드 원형 숫자 생성 함수
 * 인덱스를 유니코드 원형 숫자로 변환합니다.
 * @param {number} index - 인덱스
 * @returns {string} 유니코드 원형 숫자
 */
export const createUnicodeCircleNumber = (index: number) => {
  const startCharCode = 9312; // Circled Digit One의 유니코드 값

  if (index <= 19) {
    return String.fromCharCode(startCharCode + index);
  } else if (index <= 34) {
    return String.fromCharCode(12881 + index - 20);
  } else {
    return String.fromCharCode(12977 + index - 35);
  }
};

/**
 * 다운로드 URL 가져오기 인터페이스
 * Cognito에서 다운로드 URL을 가져오기 위한 파라미터를 정의합니다.
 * @property {string} url - URL
 * @property {IUserInfo | null} userInfo - 사용자 정보
 * @property {ICognitoType} [type] - Cognito 타입
 * @property {string} [uuid] - UUID
 */
interface IGetDownloadUrl {
  url: string;
  userInfo: IUserInfo | null;
  type?: ICognitoType;
  uuid?: string;
}

/**
 * Cognito로 URL 가져오기
 * Cognito에서 다운로드 URL을 가져옵니다.
 * @param {IGetDownloadUrl} params - 다운로드 URL 가져오기 파라미터
 * @returns {Promise<string | false>} 성공 시 URL, 실패 시 false
 */
export const getDownloadUrl = async ({
  url,
  userInfo,
  type = 'users',
  uuid,
}: IGetDownloadUrl) => {
  // eslint-disable-next-line no-useless-catch
  try {
    const value = (await s3GetCognito({
      type,
      imgUrls: [url],
      uuid: uuid ? uuid : userInfo?.user.uuid,
      userInfo,
    })) as string[];

    if (value && value.length !== 0) {
      return value[0];
    } else {
      return false;
    }
  } catch (error: any) {
    throw error;
  }
};

/**
 * 다운로드 데이터 포맷 인터페이스
 * 다운로드 데이터 포맷에 필요한 파라미터를 정의합니다.
 * @property {any[]} list - 리스트
 * @property {IUserInfo | null} userInfo - 사용자 정보
 * @property {ICognitoType} [type] - Cognito 타입
 * @property {string} [uuid] - UUID
 */
interface IFormatDownloadData extends defaultProps {
  list: any[];
  userInfo: IUserInfo | null;
  type?: ICognitoType;
  uuid?: string;
}

/**
 * 마이페이지에서 서류를 다운로드하기 위해서 cognito로 URL을 가져온 후 list에 다시 세팅
 * 리스트의 각 항목에 대해 Cognito에서 다운로드 URL을 가져와 업데이트합니다.
 * @param {IFormatDownloadData} params - 다운로드 데이터 포맷 파라미터
 * @returns {Promise<any[] | false>} 성공 시 업데이트된 리스트, 실패 시 false
 */
export const formatDownloadData = async ({
  list,
  userInfo,
  setIsLoading,
  setErrorModal,
  type,
  uuid,
}: IFormatDownloadData) => {
  setIsLoading(true);
  try {
    const updatedList = list.map(async item => {
      if ('url' in item) {
        const newUrl = await getDownloadUrl({
          url: item.url,
          userInfo,
          type,
          uuid: 'userUUID' in item ? item.userUUID : uuid,
        });

        return {...item, url: newUrl};
      }
      return item;
    });
    // 비동기 작업이 완료된 후에 updatedList를 적용하기 위해 Promise.all() 사용
    const newList = await Promise.all(updatedList);
    return newList;
  } catch (error: any) {
    return false;
  } finally {
    setIsLoading(false);
  }
};

/**
 * 검색할 카테고리(이번달, 최근 3개월)에 따른 시작일과 종료일 반환 함수
 * 검색 카테고리에 따라 시작일과 종료일을 반환합니다.
 * @param {'thisMonth' | 'last3Months'} category - 검색 카테고리
 * @returns {{startDate: Date, endDate: Date}} 시작일과 종료일
 */
export const formatBySearchDateCategory = (
  category: 'thisMonth' | 'last3Months',
) => {
  const today = new Date();
  let startDate = new Date();
  let endDate = new Date();
  if (category === 'thisMonth') {
    startDate = new Date(today.getFullYear(), today.getMonth(), 1);
    endDate = new Date(today.getFullYear(), today.getMonth() + 1, 0);
  } else {
    startDate = new Date(
      today.getFullYear(),
      today.getMonth() - 3,
      today.getDate(),
    );
  }
  return {
    startDate,
    endDate,
  };
};

/**
 pdf 를 image 로 변환하는 함수(첫 페이지만)
 @param parameter
 @returns
 @example
 ```typescript
  const result = await convertPdfToImage(File); // 이미지 파일 객체
```
 */

/**
 * 파일 데이터 읽기 함수
 * File 객체를 Data URL로 변환합니다.
 * @param {File} file - 파일 객체
 * @returns {Promise<string>} Data URL
 */
const readFileData = (file: File) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e: any) => {
      resolve(e.target.result);
    };
    reader.onerror = err => {
      reject(err);
    };
    reader.readAsDataURL(file);
  });
};

/**
 * PDF를 이미지로 변환하는 함수 (첫 페이지만)
 * PDF 파일의 첫 페이지를 이미지로 변환합니다.
 * @param {File} file - PDF 파일
 * @returns {Promise<File | null>} 변환된 이미지 파일
 */
export const convertPdfToImage = async (file: File) => {
  const PDFJS = require('pdfjs-dist/webpack');
  let images;
  const data = await readFileData(file);
  const pdf = await PDFJS.getDocument(data).promise;
  const canvas = document.createElement('canvas');
  const page = await pdf.getPage(1);
  const viewport = page.getViewport({scale: 1});
  const context = canvas.getContext('2d');
  canvas.height = viewport.height;
  canvas.width = viewport.width;
  await page.render({canvasContext: context, viewport: viewport}).promise;

  const imageData = await new Promise(resolve => {
    canvas.toBlob(blob => {
      resolve(blob);
    }, 'image/jpeg');
  });
  images = new File([imageData as Blob], file.name.replace(/\.pdf$/i, '.jpg'), {
    type: 'image/jpeg',
  });
  canvas.remove();

  return images || null;
};

/**
 * 다운로드 클릭 핸들러
 * Cognito에서 파일을 다운로드합니다.
 * @param {object} data - 다운로드 데이터
 * @param {string} data.url - URL
 * @param {string} data.name - 파일명
 * @param {IUserInfo | null} data.userInfo - 사용자 정보
 * @param {ICognitoType} data.type - Cognito 타입
 * @param {string} data.uuid - UUID
 * @returns {Promise<void>}
 */
export const handleDownloadClick = async (data: {
  url: string;
  name: string;
  userInfo: IUserInfo | null;
  type: ICognitoType;
  uuid: string;
}) => {
  try {
    const {url, userInfo, type, uuid} = data;
    const cognitoData: any = await s3GetCognito({
      imgUrls: [url],
      uuid,
      userInfo,
      type,
    });

    const response = await fetch(cognitoData, {method: 'GET'});
    const blob = await response.blob();
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = data.name;
    link.click();
    URL.revokeObjectURL(link.href);
  } catch (error) {
    // Error while downloading document
  }
};

/**
 * 썸네일용 파일 변환 함수
 * 파일명에 _thumbnail을 추가한 새 파일을 생성합니다.
 * @param {File} file - 원본 파일
 * @returns {File} 썸네일 파일
 */
export const convertThumbnailFile = (file: File) => {
  const fileNameParts = file.name.split('.');
  const nameWithoutExtension = fileNameParts.slice(0, -1).join('.');
  const extension = fileNameParts.pop();
  const copiedFileName = `${nameWithoutExtension}_thumbnail.${extension}`;
  const thumbnailFile = new File([file], copiedFileName, {
    type: file.type,
  });
  return thumbnailFile;
};

/**
 * 일반 다운로드 클릭 핸들러
 * 일반 URL에서 파일을 다운로드합니다.
 * @param {string} url - 다운로드 URL
 * @param {string} name - 파일명
 * @returns {Promise<void>}
 */
export const handleNormalDownloadClick = async (url: string, name: string) => {
  try {
    const response = await fetch(url, {method: 'GET'});
    const blob = await response.blob();
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = name;
    link.click();
    URL.revokeObjectURL(link.href);
  } catch (error) {
    // Error while downloading document
  }
};

/**
 * 엑셀 다운로드 사용량 인터페이스
 * 엑셀 다운로드 사용량에 필요한 파라미터를 정의합니다.
 * @property {IActiveUser[]} data - 활성 사용자 데이터
 * @property {IUsageDateType} dateType - 날짜 타입
 */
interface IExcelDownloadUsage {
  data: IActiveUser[];
  dateType: IUsageDateType;
}

/**
 * 사용자수 엑셀 다운로드
 * 사용자 수 데이터를 엑셀 파일로 다운로드합니다.
 * @param {IExcelDownloadUsage} params - 엑셀 다운로드 사용량 파라미터
 * @returns {Promise<void>}
 */
export const excelDownloadUsage = async ({
  data,
  dateType,
}: IExcelDownloadUsage) => {
  const usageData: UsageDataDto = {
    usageList: data,
  };

  const excelUsageData = createUsageExcelData(usageData, 'ko-KR', dateType);

  const service = new UsageRegistersService();
  await service.excelUsage(excelUsageData);
};
